import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import TabGroup from "@/components/ui/tab";
import ProgressBar from "@/components/ui/progress-bar";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Eye,
  Edit,
  Delete,
  Document,
  Employees,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import { formatDate } from "@/utils";
import type { Exam } from "../types/exam-management.types";
import { ExamStatus } from "../types/exam-management.types";
import { examManagementApi } from "../services/exam-management.api";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./ExamsPage.module.scss";

const ExamsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.EXAMS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.EXAMS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.EXAMS_DELETE);

  const [exams, setExams] = useState<Exam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stateSize = (location.state as { pageSize?: number } | null)
      ?.pageSize;
    if (typeof stateSize === "number" && stateSize > 0) return stateSize;
    const stored = sessionStorage.getItem("examManagement.pageSize");
    const storedSize = stored ? Number(stored) : NaN;
    return Number.isFinite(storedSize) && storedSize > 0 ? storedSize : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Exam[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [activeTab, setActiveTab] = useState(() => {
    const stateTab = (location.state as { activeTab?: number } | null)
      ?.activeTab;
    if (typeof stateTab === "number") return stateTab;
    const stored = sessionStorage.getItem("examManagement.activeTab");
    const storedTab = stored ? Number(stored) : NaN;
    return Number.isFinite(storedTab) ? storedTab : 0;
  });
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [deleteModalExam, setDeleteModalExam] = useState<Exam | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortValue, setSortValue] = useState<string>("publishDateDesc");
  const [educationFilterOptions, setEducationFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Tab configuration
  const tabs = [
    { label: "examManagement.tabs.assigned", content: null },
    { label: "examManagement.tabs.unassigned", content: null },
    { label: "examManagement.tabs.draft", content: null },
    { label: "examManagement.tabs.archive", content: null },
  ];

  // Map tab index to backend status (0=DRAFT, 1=ASSIGNED, 2=UNASSIGNED, 3=ARCHIVE)
  const getStatusFromTab = (tabIndex: number): number | undefined => {
    const statusMap: (number | undefined)[] = [
      ExamStatus.ASSIGNED,
      ExamStatus.UNASSIGNED,
      ExamStatus.DRAFT,
      ExamStatus.ARCHIVE,
    ];
    return statusMap[tabIndex];
  };

  // İlişkili eğitim filtresi için eğitim listesini çek (id + isim)
  useEffect(() => {
    let cancelled = false;
    educationManagementApi
      .getAll({ page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        setEducationFilterOptions(
          res.data.map((e) => ({ label: e.educationName, value: e.id })),
        );
      })
      .catch(() => {
        if (!cancelled) setEducationFilterOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const stateTab = (location.state as { activeTab?: number } | null)
      ?.activeTab;
    if (typeof stateTab === "number") {
      setActiveTab(stateTab);
      setCurrentPage(1);
      sessionStorage.setItem("examManagement.activeTab", String(stateTab));
    }
    const statePageSize = (location.state as { pageSize?: number } | null)
      ?.pageSize;
    if (typeof statePageSize === "number" && statePageSize > 0) {
      setPageSize(statePageSize);
      sessionStorage.setItem("examManagement.pageSize", String(statePageSize));
    }
  }, [location.state]);

  // Sort seçimini API parametrelerine çevir
  const sortParams = useMemo((): {
    sortBy?: "createdAt" | "name" | "successRate" | "passingScore";
    sortOrder?: "asc" | "desc";
  } => {
    switch (sortValue) {
      case "nameAsc":
        return { sortBy: "name", sortOrder: "asc" };
      case "nameDesc":
        return { sortBy: "name", sortOrder: "desc" };
      case "publishDateDesc":
        return { sortBy: "createdAt", sortOrder: "asc" };
      case "publishDateAsc":
        return { sortBy: "createdAt", sortOrder: "desc" };
      case "successRateAsc":
        return { sortBy: "successRate", sortOrder: "asc" };
      case "successRateDesc":
        return { sortBy: "successRate", sortOrder: "desc" };
      case "passingScoreDesc":
        return { sortBy: "passingScore", sortOrder: "desc" };
      case "passingScoreAsc":
        return { sortBy: "passingScore", sortOrder: "asc" };
      default:
        return { sortBy: "createdAt", sortOrder: "desc" };
    }
  }, [sortValue]);

  // Tarih aralığını YYYY-MM-DD string'e çevir
  const toYmd = (v: Date | string | null): string => {
    if (v == null) return "";
    if (v instanceof Date) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, "0");
      const d = String(v.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return typeof v === "string" ? v.split("T")[0] : "";
  };

  // Filter değerlerinden API parametreleri oluştur
  const buildFilterParams = useCallback(() => {
    const publishDate = filterValues?.publishDate;
    let createdAtStart: string | undefined;
    let createdAtEnd: string | undefined;
    if (Array.isArray(publishDate)) {
      const [from, to] = publishDate;
      createdAtStart = toYmd(from);
      createdAtEnd = toYmd(to);
    } else if (publishDate instanceof Date) {
      createdAtStart = toYmd(publishDate);
    } else if (typeof publishDate === "string" && publishDate) {
      createdAtStart = publishDate.split("T")[0];
    }

    const educationIds = Array.isArray(filterValues?.relatedEducation)
      ? filterValues.relatedEducation.filter((id: string) => !!id)
      : typeof filterValues?.relatedEducation === "string" &&
          filterValues.relatedEducation
        ? [filterValues.relatedEducation]
        : undefined;

    const passingScoreRange = filterValues?.passingScore as
      | [number, number]
      | undefined;
    const minPassingScore =
      passingScoreRange?.[0] !== undefined &&
      !Number.isNaN(Number(passingScoreRange[0]))
        ? Number(passingScoreRange[0])
        : undefined;
    const maxPassingScore =
      passingScoreRange?.[1] !== undefined &&
      !Number.isNaN(Number(passingScoreRange[1]))
        ? Number(passingScoreRange[1])
        : undefined;

    const successRateRange = filterValues?.successRate as
      | [number, number]
      | undefined;
    const minSuccessRate =
      successRateRange?.[0] !== undefined &&
      !Number.isNaN(Number(successRateRange[0]))
        ? Number(successRateRange[0])
        : undefined;
    const maxSuccessRate =
      successRateRange?.[1] !== undefined &&
      !Number.isNaN(Number(successRateRange[1]))
        ? Number(successRateRange[1])
        : undefined;

    return {
      createdAtStart: createdAtStart || undefined,
      createdAtEnd: createdAtEnd || undefined,
      educationIds: educationIds?.length ? educationIds : undefined,
      minPassingScore,
      maxPassingScore,
      minSuccessRate,
      maxSuccessRate,
    };
  }, [filterValues]);

  // Fetch exams
  const fetchExams = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = getStatusFromTab(activeTab);
      const filterParams = buildFilterParams();
      const response = await examManagementApi.getAll({
        page: currentPage,
        pageSize,
        filters: {
          search: debouncedSearch || undefined,
          status,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
          ...filterParams,
        },
      });
      setExams(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching exams:", error);
      setExams([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    activeTab,
    debouncedSearch,
    filterValues,
    sortParams,
    buildFilterParams,
  ]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    sessionStorage.setItem("examManagement.pageSize", String(size));
  };

  // Handle row selection
  const handleRowSelect = (row: Exam) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(exams);
    } else {
      setSelectedRows([]);
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [rowId]: isOpen,
    }));
  };

  // Handle add new exam
  const handleAddNew = () => {
    navigate("/dashboard/exam-management/create");
  };

  // Handle filter change
  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  // Handle tab change
  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
    sessionStorage.setItem("examManagement.activeTab", String(index));
  };

  // Handle delete
  const handleDeleteClick = (exam: Exam) => {
    setDeleteModalExam(exam);
    handleDropdownToggle(exam.id, false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalExam) return;
    setIsDeleting(true);
    try {
      await examManagementApi.delete(deleteModalExam.id);
      toast.success(
        t("examManagement.deleteSuccess"),
      );
      setDeleteModalExam(null);
      fetchExams();
    } catch {
      toast.error(
        t("examManagement.deleteError"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle archive / unarchive (archive tab → unarchive)
  const handleArchiveClick = async (exam: Exam) => {
    handleDropdownToggle(exam.id, false);
    const isArchiveTab = getStatusFromTab(activeTab) === ExamStatus.ARCHIVE;
    const targetStatus = isArchiveTab
      ? ExamStatus.ASSIGNED
      : ExamStatus.ARCHIVE;

    try {
      await examManagementApi.update(exam.id, { status: targetStatus });
      toast.success(
        isArchiveTab
          ? t("examManagement.unarchiveSuccess")
          : t("examManagement.archiveSuccess"),
      );
      fetchExams();
    } catch {
      toast.error(
        isArchiveTab
          ? t("examManagement.unarchiveError")
          : t("examManagement.archiveError"),
      );
    }
  };

  // Sort options
  const sortOptions: SortOption[] = [
    {
      label: t("examManagement.sort.nameAsc"),
      value: "nameAsc",
    },
    {
      label: t("examManagement.sort.nameDesc"),
      value: "nameDesc",
    },
    {
      label: t("examManagement.sort.publishDateDesc"),
      value: "publishDateDesc",
    },
    {
      label: t("examManagement.sort.publishDateAsc"),
      value: "publishDateAsc",
    },
    {
      label: t("examManagement.sort.successRateAsc"),
      value: "successRateAsc",
    },
    {
      label: t("examManagement.sort.successRateDesc"),
      value: "successRateDesc",
    },
    {
      label: t("examManagement.sort.passingScoreDesc"),
      value: "passingScoreDesc",
    },
    {
      label: t("examManagement.sort.passingScoreAsc"),
      value: "passingScoreAsc",
    },
  ];

  // Handle sort change
  const handleSortChange = (option: SortOption) => {
    setSortValue(option.value ?? "publishDateDesc");
    setCurrentPage(1);
  };

  // Filter fields
  const filterFields: FilterField[] = [
    {
      name: "publishDate",
      label: t("examManagement.filters.publishDate"),
      type: "daterange",
      placeholder: t("examManagement.filters.publishDatePlaceholder"),
    },
    {
      name: "relatedEducation",
      label: t("examManagement.filters.relatedEducation"),
      type: "dropdown",
      placeholder: t("examManagement.filters.relatedEducationPlaceholder"),
      options: educationFilterOptions,
      searchable: false,
      multiple: true,
    },
    // {
    //   name: "successStatus",
    //   label: t("examManagement.filters.successStatus"),
    //   type: "dropdown",
    //   placeholder: t(
    //     "examManagement.filters.successStatusPlaceholder",
    //     "Başarı durumu seç . . .",
    //   ),
    //   options: [
    //     { label: t("examManagement.filters.passed"), value: "passed" },
    //     { label: t("examManagement.filters.failed"), value: "failed" },
    //   ],
    // },
    {
      name: "passingScore",
      label: t("examManagement.filters.passingScore"),
      type: "range",
      min: 0,
      max: 100,
      unit: "Puan",
    },
    {
      name: "successRate",
      label: t("examManagement.filters.successRate"),
      type: "range",
      min: 0,
      max: 100,
      unit: "%",
    },
  ];

  // Table columns (backend: name, createdAt, relatedEducations, successRate, passedCount, totalAttempts, passingScore, questions.length)
  const columns: TableColumn<Exam>[] = [
    {
      header: t("examManagement.columns.examName"),
      accessor: "name",
      className: "exam-name-column",
    },
    {
      header: t("examManagement.columns.publishDate"),
      accessor: (row) => formatDate(row.createdAt),
      className: "publish-date-column",
    },
    {
      header: t("examManagement.columns.relatedEducation"),
      accessor: (row) =>
        row.relatedEducations?.length
          ? row.relatedEducations.map((e) => e.name).join(", ")
          : "—",
      className: "related-education-column",
    },
    {
      header: t("examManagement.columns.participantCount"),
      accessor: (row) =>
        row.participantCount != null ? String(row.participantCount) : "—",
      className: "participant-count-column",
    },
    {
      header: t("examManagement.columns.successRate"),
      accessor: (row) => (
        <ProgressBar
          value={row.successRate ?? 0}
          score={row.passedCount}
          total={row.totalAttempts}
          size="small"
        />
      ),
      className: "success-rate-column",
    },
    {
      header: t("examManagement.columns.passingGrade"),
      accessor: (row) => row.passingScore.toString(),
      className: "passing-grade-column",
    },
    {
      header: t("examManagement.columns.questionCount"),
      accessor: (row) => (row.questions?.length ?? 0).toString(),
      className: "question-count-column",
    },
  ];

  // Render actions
  const renderActions = (row: Exam) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const isArchiveTab = getStatusFromTab(activeTab) === ExamStatus.ARCHIVE;

    const menuItems = [
      {
        icon: Eye,
        label: t("examManagement.actions.viewDetail"),
        color: "#505766",
        onClick: () => {
          navigate(`/dashboard/exam-management/${row.id}/detail`, {
            state: { activeTab, pageSize },
          });
          handleDropdownToggle(rowId, false);
        },
      },
      {
        icon: Employees,
        label: t("examManagement.actions.viewAttendees"),
        color: "#505766",
        onClick: () => {
          navigate(`/dashboard/exam-management/${row.id}/attendees`, {
            state: { activeTab, pageSize },
          });
          handleDropdownToggle(rowId, false);
        },
      },
      canUpdate && {
        icon: Edit,
        label: t("examManagement.actions.edit"),
        color: "#505766",
        onClick: () => {
          navigate(`/dashboard/exam-management/${row.id}/edit`, {
            state: { activeTab, pageSize },
          });
          handleDropdownToggle(rowId, false);
        },
      },
      canUpdate && {
        icon: Document,
        label: isArchiveTab
          ? t("examManagement.actions.unarchive")
          : t("examManagement.actions.archive"),
        color: "#505766",
        onClick: () => handleArchiveClick(row),
      },
      canDelete && {
        icon: Delete,
        label: t("examManagement.actions.delete"),
        color: "#EF4444",
        isDanger: true,
        onClick: () => handleDeleteClick(row),
      },
    ].filter(Boolean) as {
      icon: React.ComponentType<{
        width?: number;
        height?: number;
        className?: string;
        color?: string;
      }>;
      label: string;
      color: string;
      isDanger?: boolean;
      onClick: () => void;
    }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              type="button"
              className={`${styles.actionMenuItem} ${
                item.isDanger ? styles.error : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
            >
              {Icon && (
                <Icon
                  width={24}
                  height={24}
                  className={styles.actionMenuIcon}
                  color={item.color}
                />
              )}
              <span className={styles.actionMenuLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("examManagement.title")} />

      <div className={styles.tabContainer}>
        <TabGroup
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />
      </div>

      <PageHeader
        searchPlaceholder={t("examManagement.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={sortValue}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleAddNew : undefined}
        addButtonLabel={
          canCreate ? t("examManagement.addButton") : undefined
        }
      />

      <Table
        columns={columns}
        data={exams}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("examManagement.noData")}
      />

      <ConfirmModal
        isOpen={!!deleteModalExam}
        onClose={() => setDeleteModalExam(null)}
        onConfirm={handleConfirmDelete}
        title={t("examManagement.deleteModal.title")}
        message={
          deleteModalExam
            ? t(
                "examManagement.deleteModal.message",
                t("exams.deleteConfirm"),
                { name: deleteModalExam.name },
              )
            : ""
        }
        confirmText={t("examManagement.deleteModal.confirm")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
      />
    </DashboardLayout>
  );
};

export default ExamsPage;
